import React, { useEffect, useState } from 'react';
import { Layout, theme, notification, Popover } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import { GapTabs } from 'store/ducks/Gaps/types';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { gapsOp } from 'store/ducks/Gaps';
import { OptionType } from 'store/ducks/Global/types';
import ApiService from 'utils/ApiCalls';
import CustomSelect from 'components/CustomSelect';
import gapsRequests from 'constants/gapsRequests';
import Helpers from 'utils/Helpers';
import i18n from 'constants/translation/i18n';
import { useTranslation } from 'react-i18next';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import { AuditFindingsLayoutProps } from 'store/ducks/Management/types';
import { globalOp } from 'store/ducks/Global';
import * as Styled from 'components/Layouts/Layout.styled';

const { Content, Sider } = Layout;
const { useToken } = theme;

const AuditFindingsLayout: React.FC<AuditFindingsLayoutProps> = ({
  activeTab,
  activeKey,
  children
}) => {
  const dispatch = useDispatch();
  const { token } = useToken();
  const { t } = useTranslation();

  const [moduleOptionsState, setModuleOptionsState] = useState<OptionType[]>(
    []
  );

  const { lang, scheme, drawTable, orgContext } = useSelector(
    (state: RootState) => ({
      lang: state.global.lang,
      scheme: state.global.scheme,
      drawTable: state.complianceEst.drawTable,
      orgContext: state.global.hierarchicalLevelsScope
    })
  );

  const industry = orgContext?.industryType || [];

  const industryArray = Array.isArray(industry)
    ? industry
    : industry
    ? [industry]
    : [];

  const safeModuleArray = Array.isArray(orgContext.module)
    ? orgContext.module
    : orgContext.module !== undefined && orgContext.module !== null
    ? [orgContext.module]
    : [];

  const selectedModuleLabels = safeModuleArray
    .map((m: any) => {
      if (typeof m === 'object' && m !== null) {
        return m.label;
      }

      const option = moduleOptionsState.find((o) => o.value === m);
      return option?.label ?? null;
    })
    .filter(Boolean);

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang]);

  const applyFilters = async () => {
    if (!lang) return;
    const baseParams = Helpers.toApiFilters(orgContext);
    const filterParams = {
      ...baseParams,
      module: selectedModuleLabels
    };

    const hasFilters = Object.keys(filterParams).length > 0;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    if (!hasFilters) return;

    if (!Object.keys(filterParams).length) {
      notification.error({
        message: 'Filters Required',
        description: 'Please fill in the filters for displaying',
        duration: 10
      });
      return;
    }

    dispatch(complianceEstOp.setDrawTable(false));
    dispatch(gapsOp.setGapsDescription([]));
    dispatch(gapsOp.setGapsTableData([]));

    const gapsReqs = gapsRequests(dispatch, lang, filterParams);

    dispatch(gapsOp.setAssignedAuditFindingsUpdated(false));
    gapsReqs[GapTabs.AUDIT_FINDINGS]();

    dispatch(complianceEstOp.setDrawTable(true));

    localStorage.setItem('orgContext', JSON.stringify(orgContext));
    // localStorage.setItem('orgContext', JSON.stringify(normalized));
    // dispatch(complianceEstOp.setSaveClicked(true));
  };

  /* ---------------- Init ---------------- */

  useEffect(() => {
    dispatch(complianceEstOp.setFilters(lang));
  }, [lang]);

  const isReadyToApplyFilters =
    !!lang &&
    !!orgContext &&
    Object.keys(orgContext).length > 0 &&
    !!industryArray?.length;

  /* ---------------- Auto-apply filters on mount ---------------- */

  useEffect(() => {
    if (!isReadyToApplyFilters) return;
    applyFilters();
  }, [isReadyToApplyFilters]);

  useEffect(() => {
    dispatch(complianceEstOp.setDrawTable(false));
  }, [activeTab, activeKey]);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const industries = industryArray;

        if (!industries.length) {
          setModuleOptionsState([]);
          return;
        }

        const ids = industries.map((i: any) => i.value ?? i);

        const responses = await Promise.all(
          ids.map((id: number) =>
            ApiService.getModulesByIndustries({
              lang,
              ind_id: id
            })
          )
        );

        const merged = responses.flatMap((r) => r?.data?.data ?? []);

        const unique = Array.from(
          new Map(merged.map((m: any) => [m.mod_id, m])).values()
        );

        setModuleOptionsState(
          unique
            .filter((m: any) => m?.mod_id != null)
            .map((m: any) => ({
              value: m.mod_id,
              label: m.module
            }))
        );
      } catch (err) {
        console.error(err);
        setModuleOptionsState([]);
      }
    };

    fetchModules();
  }, [JSON.stringify(industryArray), lang]);

  const selectedModuleValues = Array.isArray(orgContext.module)
    ? orgContext.module
    : [];

  return (
    <Styled.StyledLayout token={token}>
      <TitleBar
        title={i18n.t('AUDIT_FINDINGS_MAPPING')}
        goBackButtons={
          <>
            <GoBackButton
              to='/workingAreaDetails/audit-findings'
              text={t('GO_BACK')}
            />
            <GoBackButton to='/working_area' text={t('GO_TO_WORKING_AREAS')} />
          </>
        }
      />
      <Styled.HorizontalFilterBar token={token} scheme={scheme}>
        {!industryArray.length ? (
          <Popover
            content="Please select Industry in 'Who Am I' to enable Modules"
            placement='top'
          >
            <div>
              <CustomSelect disabled mode='multiple' />
            </div>
          </Popover>
        ) : (
          <CustomSelect
            size='md'
            mode='multiple'
            options={moduleOptionsState}
            value={selectedModuleValues}
            onChange={(value: any[]) => {
              dispatch(
                globalOp.setHierarchicalLevelsScope({
                  ...orgContext,
                  module: (value || [])
                    .map((v) => {
                      const option = moduleOptionsState.find(
                        (o) => o.value === (typeof v === 'object' ? v.value : v)
                      );

                      return option
                        ? { value: option.value, label: option.label }
                        : null;
                    })
                    .filter(Boolean)
                })
              );
            }}
          />
        )}
        <Styled.ActionButtons
          isDataManagementPage={true}
          hasDisplayedData={drawTable}
        >
          <Styled.ApplyButton
            type='primary'
            onClick={applyFilters}
            scheme={scheme}
          >
            {t('DISPLAY')}
          </Styled.ApplyButton>
        </Styled.ActionButtons>
      </Styled.HorizontalFilterBar>
      <Styled.StyledLayout token={token}>
        <Layout>
          <Content>{children}</Content>
        </Layout>
        <Sider />
      </Styled.StyledLayout>
    </Styled.StyledLayout>
  );
};

export default AuditFindingsLayout;
